import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Compass, Home, Inbox, CalendarCheck, User as UserIcon, LogOut, Plus,
  Sparkles, ShoppingBag, Briefcase, Tag, Key, ChevronDown, Settings, Menu, X,
  Rss, LayoutDashboard, Users, Handshake, CalendarDays,
} from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";
import LangSwitcher from "@/components/LangSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { useI18n } from "@/lib/i18n";

const PRIMARY = [
  { to: "/discover",    icon: Compass,    labelKey: null, fallback: "Discover",    testid: "nav-discover" },
  { to: "/feed",        icon: Rss,        labelKey: null, fallback: "Feed",        testid: "nav-feed" },
  { to: "/services",    icon: Sparkles,   labelKey: null, fallback: "Services",    testid: "nav-services" },
  { to: "/marketplace", icon: ShoppingBag, labelKey: null, fallback: "Marketplace", testid: "nav-marketplace" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  // Close avatar menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const onLogout = async () => { setMenuOpen(false); await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-40 glass border-b border-[#E5E5E3]/60" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-[64px] flex items-center gap-2 sm:gap-4">
        {/* Logo — always visible, never shrinks */}
        <Link to="/" className="flex items-center flex-shrink-0" data-testid="nav-logo" aria-label="Hoomanz — Home">
          <img src="/brand/hoomanz-logo.png" alt="Hoomanz"
            className="h-8 sm:h-9 w-auto object-contain select-none"/>
        </Link>

        {/* Hamburger — sits right next to the logo on mobile for easy thumb reach */}
        <button data-testid="nav-mobile-toggle"
          onClick={() => setMobileOpen((v)=>!v)}
          className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-[#F2F2F0] -ml-1"
          aria-label="Open menu" aria-expanded={mobileOpen}>
          {mobileOpen ? <X size={18}/> : <Menu size={18}/>}
        </button>

        {/* Primary nav — centered, with sensible breakpoints */}
        <nav className="hidden lg:flex items-center gap-0.5 mx-auto" data-testid="nav-primary">
          {PRIMARY.map(({ to, icon: Icon, labelKey, fallback, testid }) => (
            <NavLink key={to} to={to} data-testid={testid}
              className={({ isActive }) => `pill-link ${isActive ? "active" : ""}`}>
              <Icon size={15}/><span>{labelKey ? t(labelKey) : fallback}</span>
            </NavLink>
          ))}
        </nav>

        {/* Right cluster — compact.
            On phones (<sm) we hide the language + currency switchers to keep
            "Log in" and "Join Hoomanz" fully on-screen; both switchers are still
            reachable from the mobile drawer below. */}
        <div className="flex items-center gap-1 sm:gap-1.5 ml-auto lg:ml-0">
          <div className="hidden sm:flex items-center gap-1.5"><LangSwitcher /><CurrencySwitcher /></div>
          {user ? (
            <>
              <NotificationsBell />
              <Link to="/post/new" className="btn-ghost !hidden sm:!inline-flex" data-testid="nav-create-post">
                <Plus size={16}/>
                <span className="hidden md:inline">{t("nav.post")}</span>
              </Link>

              {/* Avatar dropdown — collapses Messages / Bookings / Promote / Profile / Logout */}
              <div className="relative" ref={menuRef}>
                <button
                  data-testid="nav-avatar-menu"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full hover:bg-[#F2F2F0] transition-colors"
                  aria-haspopup="menu" aria-expanded={menuOpen}>
                  {user.avatar
                    ? <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover bg-[#E5E5E3]" />
                    : <span className="h-8 w-8 rounded-full bg-[#E5E5E3] flex items-center justify-center"><UserIcon size={16}/></span>}
                  <ChevronDown size={13} className={`text-[#4A4A4A] transition-transform ${menuOpen ? "rotate-180" : ""}`}/>
                </button>
                {menuOpen && (
                  <div role="menu" data-testid="nav-avatar-dropdown"
                    className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-lg border border-[#E5E5E3] overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#E5E5E3]">
                      <p className="font-medium text-sm truncate">{user.name}</p>
                      <p className="text-xs text-[#8A8A8A] truncate">{user.email}</p>
                    </div>
                    <DropdownItem to={`/u/${user.id}`} icon={<UserIcon size={14}/>} label="My profile" testid="menu-profile" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/dashboard" icon={<LayoutDashboard size={14}/>} label="Dashboard" testid="menu-dashboard" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/groups" icon={<Users size={14}/>} label="Groups" testid="menu-groups" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/collabs" icon={<Handshake size={14}/>} label="Collabs" testid="menu-collabs" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/meetups" icon={<CalendarDays size={14}/>} label="Meetups" testid="menu-meetups" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/feed" icon={<Rss size={14}/>} label="Feed" testid="menu-feed" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/shop" icon={<ShoppingBag size={14}/>} label="Shop products" testid="menu-shop" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/messages" icon={<Inbox size={14}/>} label={t("nav.messages")} testid="menu-messages" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/bookings" icon={<CalendarCheck size={14}/>} label={t("nav.bookings")} testid="menu-bookings" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/promote" icon={<Sparkles size={14}/>} label={t("nav.promote")} testid="menu-promote" onClick={()=>setMenuOpen(false)}/>
                    <DropdownItem to="/profile/edit" icon={<Settings size={14}/>} label="Settings" testid="menu-settings" onClick={()=>setMenuOpen(false)}/>
                    <button onClick={onLogout} data-testid="menu-logout"
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-[#F2F2F0] border-t border-[#E5E5E3]">
                      <LogOut size={14}/> <span>Log out</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost !px-2.5 sm:!px-4 text-xs sm:text-sm" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/register" className="btn-primary !px-3 sm:!px-4 !text-xs sm:!text-sm" data-testid="nav-signup">
                <span className="sm:hidden">Join</span>
                <span className="hidden sm:inline">{t("nav.signup")}</span>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile / tablet drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#E5E5E3]/60 bg-white" data-testid="nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {PRIMARY.map(({ to, icon: Icon, labelKey, fallback, testid }) => (
              <NavLink key={to} to={to} data-testid={`m-${testid}`} onClick={()=>setMobileOpen(false)}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-full text-sm ${isActive ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F2F2F0]"}`}>
                <Icon size={15}/><span>{labelKey ? t(labelKey) : fallback}</span>
              </NavLink>
            ))}
          </div>
          {/* Language + Currency — hidden from the top bar on phones, exposed here */}
          <div className="sm:hidden max-w-7xl mx-auto px-4 pb-3 flex items-center gap-2 border-t border-[#E5E5E3]/60 pt-3" data-testid="nav-mobile-prefs">
            <LangSwitcher />
            <CurrencySwitcher />
          </div>
        </div>
      )}
    </header>
  );
}

function DropdownItem({ to, icon, label, testid, onClick }) {
  return (
    <Link to={to} role="menuitem" data-testid={testid} onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F2F2F0]">
      {icon}<span>{label}</span>
    </Link>
  );
}
